import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import CardCoverImage from '../CardCoverImage';
import HowItWorksModal from '../HowItWorksModal';

// The three covers are fanned, not stacked: two behind at opposing angles, the chosen one in front
// and slightly larger. Index order matches the order they are handed in. The spread narrows on a
// phone (38px) so the three covers still read as a fan inside a 158px band.
const fanTransform = (index, spread) =>
  [
    `translate(-50%,-50%) translateX(-${spread}px) rotate(-13deg)`,
    `translate(-50%,-50%) translateX(${spread}px) rotate(13deg)`,
    'translate(-50%,-50%) rotate(-1deg) scale(1.06)',
  ][index];

/**
 * The store-home invitation (design: CardStoreApp.dc.html vRevised, the band between the obituary
 * strip and the first rail).
 *
 * The point of the surface: a shopper arriving at a store of finished cards has no reason to guess
 * that they can have one made. The rails merchandise what exists; this says what does not exist yet,
 * SHOWS three real covers so the offer is visible rather than described, and names the reassurances
 * (time, stock, delivery) at the moment the decision is made.
 *
 * `fanCards` are real catalogue cards picked by the caller with the same per-session seed the rails
 * use, so the fan is stable while browsing and different on the next visit. Fewer than three (a thin
 * catalogue, a filtered view) renders fewer cards; none renders the copy alone.
 */
const InviteBand = ({ fanCards = [] }) => {
  const [howOpen, setHowOpen] = useState(false);
  const fan = fanCards.slice(0, 3);

  return (
    <>
      <Box
        data-testid="invite-band"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '16px',
          background:
            'radial-gradient(120% 130% at 82% 0%, #2b8298 0%, #165c75 40%, #0b3849 100%)',
          boxShadow: '0 14px 40px rgba(12,45,60,0.3)',
          '@keyframes inviteGlide': {
            '0%, 100%': { transform: 'translateX(-30%)' },
            '50%': { transform: 'translateX(130%)' },
          },
          '@keyframes inviteTwinkle': {
            '0%, 100%': { opacity: 0.25 },
            '50%': { opacity: 1 },
          },
        }}
      >
        {/* Slow specular sweep + a few sparks: the only decoration, and it carries the "made for you,
            one of one" idea the copy states. Both are aria-hidden and both stop on reduced motion. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: '-60%',
            left: '-20%',
            width: '80%',
            height: '220%',
            background:
              'linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.13) 45%, rgba(240,217,162,0.16) 55%, rgba(255,255,255,0) 100%)',
            animation: 'inviteGlide 7s ease-in-out infinite',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
          }}
        />
        {[
          { top: '14%', left: '6%', color: '#f0d9a2', fontSize: 12, delay: '0s' },
          { top: '66%', left: '14%', color: '#cfe9f4', fontSize: 8, delay: '1s' },
          { top: '24%', right: '40%', color: '#f0d9a2', fontSize: 9, delay: '2s' },
        ].map((spark) => (
          <Box
            key={`${spark.top}-${spark.left || spark.right}`}
            aria-hidden
            component="span"
            sx={{
              position: 'absolute',
              top: spark.top,
              left: spark.left,
              right: spark.right,
              color: spark.color,
              fontSize: spark.fontSize,
              lineHeight: 1,
              animation: `inviteTwinkle 3.2s ease-in-out ${spark.delay} infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            ✦
          </Box>
        ))}

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 2.25, md: 4 },
            flexWrap: 'wrap',
            p: { xs: '20px 18px 24px', md: '40px 44px' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 250 } }}>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#fbf1d9',
                mb: 1.5,
              }}
            >
              One of one
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: "'Tinos','Times New Roman',Times,serif",
                fontSize: { xs: '1.7rem', md: '2.5rem' },
                fontWeight: 700,
                lineHeight: 1.18,
                color: '#fff',
                m: '0 0 12px',
                letterSpacing: '-0.01em',
                textWrap: 'pretty',
              }}
            >
              A card that has never
              <Box component="br" sx={{ display: { xs: 'none', md: 'inline' } }} /> existed before.
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.9rem', md: '1rem' },
                color: 'rgba(255,255,255,0.86)',
                lineHeight: 1.6,
                m: '0 0 22px',
                maxWidth: '44ch',
                textWrap: 'pretty',
              }}
            >
              Tell us the feeling — a place they loved, a private joke, the way they laughed. We design
              the cover, print it on real card stock, and mail it for you.
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={() => setHowOpen(true)}
              data-testid="invite-band-cta"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.125,
                whiteSpace: 'nowrap',
                font: 'inherit',
                fontSize: '0.95rem',
                fontWeight: 700,
                color: '#0e4459',
                backgroundColor: '#f0d9a2',
                border: 0,
                borderRadius: '26px',
                p: '14px 26px',
                cursor: 'pointer',
                boxShadow: '0 6px 20px rgba(240,217,162,0.28)',
                '&:hover': { backgroundColor: '#f6e6bf' },
              }}
            >
              <AutoAwesomeIcon aria-hidden sx={{ fontSize: 20 }} />
              Design your own card
            </Box>
            <Typography
              sx={{
                fontSize: '0.8rem',
                color: 'rgba(255,255,255,0.85)',
                mt: 1.75,
                letterSpacing: '0.02em',
              }}
            >
              Ready in minutes · Printed on real stock · Free delivery
            </Typography>
          </Box>

          {fan.length > 0 && (
            <Box
              aria-hidden
              sx={{
                flex: '0 0 auto',
                // On a phone the fan leads: the covers are the argument, the words follow them.
                order: { xs: -1, md: 0 },
                position: 'relative',
                width: { xs: '100%', md: 340 },
                height: { xs: 158, md: 280 },
              }}
            >
              {fan.map((card, i) => (
                <CardCoverImage
                  key={card.id}
                  cardId={card.id}
                  imageId={card.coverImageId}
                  coverImageUrl={card.coverImageUrl}
                  alt=""
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: { xs: 98, md: 148 },
                    height: 'auto',
                    aspectRatio: '5 / 7',
                    objectFit: 'cover',
                    borderRadius: '10px',
                    transform: { xs: fanTransform(i, 38), md: fanTransform(i, 58) },
                    boxShadow: '0 16px 34px rgba(6,30,40,0.45)',
                    border: '1px solid rgba(255,255,255,0.14)',
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
      <HowItWorksModal open={howOpen} onClose={() => setHowOpen(false)} />
    </>
  );
};

export default InviteBand;
